// =====================================================================================
// INVRT Radar - web console - operator access section (Story PS1 rebuild)
//
// Access is what an organisation can use, and who in it: server-backed module
// subscriptions, user grants and campaign subscriptions, read from the reviewed E04A
// pagers. Desktop renders the canonical DataTable mirror; narrow viewports render the
// canonical MobileRows mirror; status renders as canonical Chips; effective intervals
// render as humanised dates through lib/format, never raw ISO strings.
//
// Pagination law (Story PS1): each list is consumed page by page with the server's REAL
// cursors (limit 25, a page a person can actually read) - never read-all-then-render.
// Every assignment row carries a plain-language why-line: who holds what, since when, and
// why it is in its current state, derived ONLY from the fields the E04A pagers publish
// (the pagers carry no granted_by or reason field, so no grantor is invented; the audit
// trail stays on the server). The assignment dialog chooses modules and campaigns from
// the E03 catalogue instead of typing raw UUIDs; its choosers need complete option sets,
// so opening the dialog walks those pagers once, on demand.
// =====================================================================================

import { useEffect, useMemo, useRef, useState, type ChangeEvent, type Dispatch, type FormEvent, type ReactNode, type SetStateAction } from "react";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { EmptyState, ErrorState, LoadingState } from "@/components/States";
import { Button } from "@/components/ui/button";
import { Chip, type ChipSignal } from "@/components/ui/chip";
import { DataTable, type DataTableRow } from "@/components/ui/data-table";
import { Dialog } from "@/components/ui/dialog";
import { Input, Select } from "@/components/ui/input";
import { MobileRows, type MobileRow } from "@/components/ui/mobile-rows";
import { Pagination } from "@/components/ui/pagination";
import { useToast } from "@/components/ui/toast";
import { useApiClient } from "@/hooks/useApi";
import { ApiError, isApiError } from "@/lib/api";
import { formatDateTime } from "@/lib/format";
import { readCatalogueKind } from "@/pages/operator/catalogueReader";
import { ClientDeliveryCoverage } from "./ClientDeliveryCoverage";
import { relinkCoverageNotice } from "./relinkCoverage";
import { useOperatorOrganisations } from "@/pages/operator/useOperatorOrganisations";
import type {
  AccessStatus,
  AdminCampaignSubscription,
  AdminMember,
  AdminModuleSubscription,
  AdminUserModuleGrant,
} from "@/lib/types.entitlements";

type AssignmentKind = "module" | "grant" | "campaign";
type DialogState = { action: "create"; kind: AssignmentKind } | {
  action: "edit" | "revoke" | "relink";
  kind: AssignmentKind;
  row: AdminModuleSubscription | AdminUserModuleGrant | AdminCampaignSubscription;
};

interface AccessData {
  members: AdminMember[];
  modules: AdminModuleSubscription[];
  grants: AdminUserModuleGrant[];
  campaigns: AdminCampaignSubscription[];
}

interface FormState {
  kind: AssignmentKind;
  moduleId: string;
  moduleSubscriptionId: string;
  memberId: string;
  campaignId: string;
  status: "active" | "suspended";
  effectiveFrom: string;
  effectiveTo: string;
  reason: string;
}

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const DATE_TIME_PATTERN = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2})$/;

export function invalidContract(message: string): ApiError {
  return new ApiError({ token: "internal_error", message, httpStatus: 502, correlationId: null });
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function hasExactKeys(value: Record<string, unknown>, keys: readonly string[]): boolean {
  const received = Object.keys(value).sort();
  const expected = [...keys].sort();
  return received.length === expected.length && received.every((key, index) => key === expected[index]);
}

export function isUuid(value: unknown): value is string {
  return typeof value === "string" && UUID_PATTERN.test(value);
}

export function isDateTime(value: unknown): value is string {
  if (typeof value !== "string" || !DATE_TIME_PATTERN.test(value)) return false;
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(?:\.\d+)?(Z|[+-]\d{2}:\d{2})$/);
  if (!match) return false;
  const year = match[1]!;
  const month = match[2]!;
  const day = match[3]!;
  const hour = match[4]!;
  const minute = match[5]!;
  const second = match[6]!;
  const offset = match[7]!;
  const y = Number(year);
  const m = Number(month);
  const d = Number(day);
  if (m < 1 || m > 12 || hour > "23" || minute > "59" || second > "59") return false;
  const daysInMonth = new Date(Date.UTC(y, m, 0)).getUTCDate();
  if (d < 1 || d > daysInMonth) return false;
  if (offset !== "Z") {
    const offsetHours = Number(offset.slice(1, 3));
    const offsetMinutes = Number(offset.slice(4, 6));
    if (offsetHours > 23 || offsetMinutes > 59) return false;
  }
  return true;
}

function isCursor(value: unknown): value is string | null {
  return value === null || (typeof value === "string" && value.length >= 1 && value.length <= 1024);
}

function isPage(value: unknown, key: string, row: (candidate: unknown) => boolean): value is Record<string, unknown> {
  return isRecord(value) && Object.keys(value).length === 2 && key in value && "next_cursor" in value && Array.isArray(value[key]) && value[key].every(row) && isCursor(value.next_cursor);
}

function isMember(value: unknown): value is AdminMember {
  return isRecord(value) && hasExactKeys(value, ["user_id", "email", "full_name", "created_at"]) && isUuid(value.user_id) && (typeof value.email === "string" || value.email === null) && (typeof value.full_name === "string" || value.full_name === null) && isDateTime(value.created_at);
}

function isAccessStatus(value: unknown): value is AccessStatus {
  return value === "active" || value === "suspended" || value === "revoked";
}

function isModuleSubscription(value: unknown): value is AdminModuleSubscription {
  return isRecord(value) && hasExactKeys(value, ["id", "org_id", "module_id", "module_code", "module_name", "status", "effective_from", "effective_to", "version", "created_at"]) && isUuid(value.id) && isUuid(value.org_id) && isUuid(value.module_id) && typeof value.module_code === "string" && typeof value.module_name === "string" && isAccessStatus(value.status) && isDateTime(value.effective_from) && (value.effective_to === null || isDateTime(value.effective_to)) && Number.isSafeInteger(value.version) && Number(value.version) > 0 && isDateTime(value.created_at);
}

function isGrant(value: unknown): value is AdminUserModuleGrant {
  return isRecord(value) && hasExactKeys(value, ["id", "org_id", "user_id", "email", "full_name", "module_id", "module_subscription_id", "module_code", "module_name", "status", "effective_from", "effective_to", "version", "created_at"]) && isUuid(value.id) && isUuid(value.org_id) && isUuid(value.user_id) && isUuid(value.module_id) && isUuid(value.module_subscription_id) && (typeof value.email === "string" || value.email === null) && (typeof value.full_name === "string" || value.full_name === null) && typeof value.module_code === "string" && typeof value.module_name === "string" && isAccessStatus(value.status) && isDateTime(value.effective_from) && (value.effective_to === null || isDateTime(value.effective_to)) && Number.isSafeInteger(value.version) && Number(value.version) > 0 && isDateTime(value.created_at);
}

export function isCampaign(value: unknown): value is AdminCampaignSubscription {
  return isRecord(value) && hasExactKeys(value, ["id", "org_id", "module_subscription_id", "module_id", "module_code", "module_name", "campaign_id", "campaign_code", "campaign_name", "status", "effective_from", "effective_to", "version", "created_at"]) && isUuid(value.id) && isUuid(value.org_id) && isUuid(value.module_subscription_id) && isUuid(value.module_id) && isUuid(value.campaign_id) && typeof value.module_code === "string" && typeof value.module_name === "string" && typeof value.campaign_code === "string" && typeof value.campaign_name === "string" && isAccessStatus(value.status) && isDateTime(value.effective_from) && (value.effective_to === null || isDateTime(value.effective_to)) && Number.isSafeInteger(value.version) && Number(value.version) > 0 && isDateTime(value.created_at);
}

/** The campaign delivery-link verdict the server prints after its own commit. */
type DeliveryVerdict = { link: "anchor"; ok: boolean } | { link: "unanchor"; ok: boolean; reason?: RetireReason };
type MutationAction = "create" | "edit" | "revoke";

/** The reviewed retire-failure tokens the server may print on `unanchor.reason` (retired false only). */
const RETIRE_REASON_COPY = {
  binding_retire_refused_by_high_intent: "The delivery partner refused to close the link, so it may still be open.",
  binding_retire_provider_unavailable: "The delivery partner could not be reached, so the link may still be open.",
  binding_retire_provider_timeout: "The delivery partner did not answer in time, so the link may still be open.",
  unmapped_committed_row_unusable: "This subscription could not be matched to a delivery link, so none was closed.",
} as const;
type RetireReason = keyof typeof RETIRE_REASON_COPY;

function isRetireReason(value: unknown): value is RetireReason {
  return typeof value === "string" && Object.prototype.hasOwnProperty.call(RETIRE_REASON_COPY, value);
}

function nonEmptyString(value: unknown): boolean {
  return typeof value === "string" && value.length > 0;
}

/** `anchor` rides campaign-subscription CREATE only: `{anchored:false}` or the full anchored echo. */
function readAnchorVerdict(value: unknown): DeliveryVerdict | null {
  if (!isRecord(value)) return null;
  if (hasExactKeys(value, ["anchored"]) && value.anchored === false) return { link: "anchor", ok: false };
  if (hasExactKeys(value, ["anchored", "high_intent_org_id", "high_intent_campaign_key", "inserted"]) && value.anchored === true && nonEmptyString(value.high_intent_org_id) && nonEmptyString(value.high_intent_campaign_key) && typeof value.inserted === "boolean") return { link: "anchor", ok: true };
  return null;
}

/** `unanchor` rides campaign-subscription REVOKE only: `{retired:false}` or the full retired echo. */
function readUnanchorVerdict(value: unknown): DeliveryVerdict | null {
  if (!isRecord(value)) return null;
  if (hasExactKeys(value, ["retired"]) && value.retired === false) return { link: "unanchor", ok: false };
  if (hasExactKeys(value, ["retired", "reason"]) && value.retired === false && isRetireReason(value.reason)) return { link: "unanchor", ok: false, reason: value.reason };
  if (hasExactKeys(value, ["retired", "high_intent_org_id", "high_intent_campaign_key"]) && value.retired === true && nonEmptyString(value.high_intent_org_id) && nonEmptyString(value.high_intent_campaign_key)) return { link: "unanchor", ok: true };
  return null;
}

/**
 * Fail-closed response reader. `subscription` is always required and strictly shaped. The one
 * permitted optional sibling is the campaign delivery-link verdict: `anchor` on create,
 * `unanchor` on revoke, and only for campaign subscriptions. Returns null on any other shape;
 * `{ verdict: null }` means a valid response that printed no verdict.
 */
export function readMutationResponse(value: unknown, kind: AssignmentKind, action: MutationAction): { verdict: DeliveryVerdict | null } | null {
  if (!isRecord(value) || !isMutationAssignment(value.subscription, kind)) return null;
  const siblings = Object.keys(value).filter((key) => key !== "subscription");
  if (siblings.length === 0) return { verdict: null };
  if (kind !== "campaign" || siblings.length !== 1) return null;
  if (action === "create" && siblings[0] === "anchor") {
    const verdict = readAnchorVerdict(value.anchor);
    return verdict ? { verdict } : null;
  }
  if (action === "revoke" && siblings[0] === "unanchor") {
    const verdict = readUnanchorVerdict(value.unanchor);
    return verdict ? { verdict } : null;
  }
  return null;
}

/** What a re-link under a restricted coverage reports about that coverage (H-1). */
export type RelinkCoverage = { applied: true } | { applied: false; held: boolean; token: string };

const RELINK_BASE_KEYS = ["anchored", "inserted", "subscription_id", "high_intent_org_id", "high_intent_campaign_key", "correlation_id"];
const RELINK_COVERAGE_KEYS = ["scope_applied", "delivery_held", "scope_token"];

/**
 * Re-link answer: the anchored echo of POST .../campaign-subscriptions/:id/anchor, or null.
 * The six base keys are required, and so is coverage: the server ALWAYS reports it on a 200,
 * so an answer with no coverage fields is a contract error (fail closed), never success. The
 * shapes the server sends: scope_applied true (delivery_held absent or false, no token), or
 * scope_applied false with BOTH delivery_held (boolean) and scope_token. Anything else is
 * refused.
 */
export function readRelinkResponse(value: unknown, subscriptionId: string): { verdict: DeliveryVerdict; coverage: RelinkCoverage } | null {
  if (!isRecord(value)) return null;
  const keys = Object.keys(value);
  if (!RELINK_BASE_KEYS.every((key) => keys.includes(key)) || !keys.every((key) => RELINK_BASE_KEYS.includes(key) || RELINK_COVERAGE_KEYS.includes(key))) return null;
  if (value.anchored !== true || typeof value.inserted !== "boolean" || value.subscription_id !== subscriptionId) return null;
  if (!nonEmptyString(value.high_intent_org_id) || !nonEmptyString(value.high_intent_campaign_key) || !nonEmptyString(value.correlation_id)) return null;
  const verdict: DeliveryVerdict = { link: "anchor", ok: true };
  const { scope_applied: applied, delivery_held: held, scope_token: token } = value;
  if (applied === true) return (held === undefined || held === false) && token === undefined ? { verdict, coverage: { applied: true } } : null;
  if (applied === false && typeof held === "boolean" && typeof token === "string" && token.length > 0) return { verdict, coverage: { applied: false, held, token } };
  return null;
}

/** The coverage a Re-link answer carried, or undefined when it carried none. */
function relinkCoverageOf(response: unknown): RelinkCoverage | undefined {
  return isRecord(response) && "coverage" in response ? (response.coverage as RelinkCoverage | undefined) : undefined;
}

function isMutationAssignment(value: unknown, kind: AssignmentKind): boolean {
  if (!isRecord(value)) return false;
  const base = ["id", "org_id", "module_id", "status", "effective_from", "effective_to", "version", "created_at", "updated_at"];
  const keys = kind === "module"
    ? base
    : kind === "grant"
      ? [...base, "module_subscription_id", "user_id"]
      : [...base, "module_subscription_id", "campaign_id"];
  const validStatus = kind === "grant" ? value.status === "active" || value.status === "revoked" : isAccessStatus(value.status);
  return hasExactKeys(value, keys) && isUuid(value.id) && isUuid(value.org_id) && isUuid(value.module_id) && (kind !== "grant" || (isUuid(value.module_subscription_id) && isUuid(value.user_id))) && (kind !== "campaign" || (isUuid(value.module_subscription_id) && isUuid(value.campaign_id))) && validStatus && isDateTime(value.effective_from) && (value.effective_to === null || isDateTime(value.effective_to)) && Number.isSafeInteger(value.version) && Number(value.version) > 0 && isDateTime(value.created_at) && isDateTime(value.updated_at);
}

function isCurrentActiveSubscription(row: AdminModuleSubscription): boolean {
  const now = Date.now();
  const endsAt = row.effective_to ? Date.parse(row.effective_to) : Number.POSITIVE_INFINITY;
  return row.status === "active" && Date.parse(row.effective_from) <= now && now < endsAt;
}

function formError(state: DialogState, values: FormState, orgId: string): string | null {
  const reason = values.reason.trim();
  if (reason.length < 3 || reason.length > 500) return "Provide a reason between 3 and 500 characters. No change was made.";
  if (state.action === "revoke" || state.action === "relink") return null;
  if (!isUuid(orgId) || !isDateTime(values.effectiveFrom)) return "Provide a valid organisation and RFC3339 effective-from date-time. No change was made.";
  if (values.effectiveTo && (!isDateTime(values.effectiveTo) || Date.parse(values.effectiveTo) <= Date.parse(values.effectiveFrom))) return "Effective to must be a valid RFC3339 date-time after effective from. No change was made.";
  if (!isUuid(values.moduleId)) return "Choose the module to subscribe. No change was made.";
  const kind = mutationKind(state, values);
  if (kind === "grant" && (!isUuid(values.memberId) || !isUuid(values.moduleSubscriptionId))) return "Choose a valid member and current module subscription. No change was made.";
  if (kind === "campaign" && (!isUuid(values.campaignId) || !isUuid(values.moduleSubscriptionId))) return "Choose a valid campaign and current module subscription. No change was made.";
  return null;
}

export async function readAll<T>(
  api: ReturnType<typeof useApiClient>,
  path: string,
  key: string,
  validRow: (candidate: unknown) => candidate is T,
): Promise<T[]> {
  const rows: T[] = [];
  const seen = new Set<string>();
  let cursor: string | undefined;
  for (;;) {
    const response = await api.get<unknown>(path, { requiresOrg: false, includeOrg: false, query: { limit: 100, cursor } });
    if (!isPage(response, key, validRow)) throw invalidContract(`The ${key} response did not match the reviewed E04A contract.`);
    rows.push(...(response[key] as T[]));
    const next = response.next_cursor;
    if (next === null) return rows;
    if (typeof next !== "string") throw invalidContract(`The ${key} response pagination cursor was invalid.`);
    if (seen.has(next)) throw invalidContract(`The ${key} response repeated a pagination cursor.`);
    seen.add(next);
    cursor = next;
  }
}

function initialForm(kind: AssignmentKind, data?: AccessData, row?: AdminModuleSubscription | AdminUserModuleGrant | AdminCampaignSubscription): FormState {
  const subscription = data?.modules.find(isCurrentActiveSubscription);
  const member = data?.members[0];
  const moduleSubscriptionId = row && "module_subscription_id" in row ? row.module_subscription_id : subscription?.id ?? "";
  const memberId = row && "user_id" in row ? row.user_id : member?.user_id ?? "";
  const campaignId = row && "campaign_id" in row ? row.campaign_id : "";
  return {
    kind,
    moduleId: row?.module_id ?? subscription?.module_id ?? "",
    moduleSubscriptionId,
    memberId,
    campaignId,
    status: row?.status === "suspended" ? "suspended" : "active",
    effectiveFrom: row?.effective_from ?? "",
    effectiveTo: row?.effective_to ?? "",
    reason: "",
  };
}

function errorCopy(error: unknown): string {
  if (!isApiError(error)) return "The access change could not be completed. No change was made.";
  if (error.httpStatus === 403) return "The server refused this access change. No change was made.";
  if (error.token === "access_assignment_version_conflict_or_not_found" || error.httpStatus === 409) return "This access record changed after you opened it. Refresh the organisation and try again. No change was made.";
  if (error.token === "access_assignment_invalid_hierarchy_or_interval" || error.token === "access_assignment_invalid_interval") return "The access hierarchy or effective interval is not valid. No change was made.";
  if (error.token === "campaign_anchor_failed") return "The delivery link could not be attached. No change was made. Try again or contact support.";
  return "The access change could not be completed. No change was made.";
}

function statusSignal(status: AccessStatus): ChipSignal | undefined {
  if (status === "active") return "buy";
  if (status === "suspended") return "pace";
  return "risk";
}

function kindLabel(kind: AssignmentKind): string {
  return kind === "module" ? "module subscription" : kind === "grant" ? "user grant" : "campaign subscription";
}

function mutationKind(state: DialogState, values: FormState): AssignmentKind {
  return state.action === "create" ? values.kind : state.kind;
}

function assignmentName(row: AdminModuleSubscription | AdminUserModuleGrant | AdminCampaignSubscription): string {
  return "campaign_name" in row
    ? `campaign subscription ${row.campaign_code}`
    : "email" in row
      ? `user grant ${row.email ?? row.id}`
      : `module subscription ${row.module_code}`;
}

/** The row's display name: email for grants, the campaign or module name otherwise. */
function assignmentDisplayName(row: AdminModuleSubscription | AdminUserModuleGrant | AdminCampaignSubscription): string {
  return "campaign_name" in row
    ? row.campaign_name
    : "email" in row
      ? row.email ?? "Unnamed member"
      : row.module_name;
}

function assignmentCode(row: AdminModuleSubscription | AdminUserModuleGrant | AdminCampaignSubscription): string {
  return "campaign_code" in row ? row.campaign_code : row.module_code;
}

/**
 * The plain-language why-line for an assignment row: who holds what, since when, and why
 * it is in its current state. Derived only from the fields the E04A pagers publish - the
 * pagers carry no granted_by or reason field, so no grantor is ever invented here.
 */
function whyLine(row: AdminModuleSubscription | AdminUserModuleGrant | AdminCampaignSubscription): string {
  const interval = row.effective_to
    ? `from ${formatDateTime(row.effective_from)} until ${formatDateTime(row.effective_to)}`
    : `from ${formatDateTime(row.effective_from)}, open-ended`;
  if ("campaign_name" in row) {
    return `${row.campaign_name} is subscribed under ${row.module_name} (${row.status}) ${interval}.`;
  }
  if ("user_id" in row) {
    const who = row.full_name
      ? row.email
        ? `${row.full_name} (${row.email})`
        : row.full_name
      : row.email ?? "This member";
    return `${who} holds ${row.module_name} (${row.status}) ${interval}.`;
  }
  return `${row.module_name} is ${row.status} for the whole organisation ${interval}.`;
}

// ---- the real-cursor pager -----------------------------------------------------------------

interface AccessPager<T> {
  rows: T[];
  isPending: boolean;
  isError: boolean;
  error: unknown;
  page: number;
  hasNext: boolean;
  hasPrevious: boolean;
  goNext: () => void;
  goPrevious: () => void;
  refetch: () => void;
}

function useAccessPage<T>(
  orgId: string,
  path: string,
  keyName: string,
  isValidRow: (candidate: unknown) => candidate is T,
): AccessPager<T> {
  const api = useApiClient();
  const [cursors, setCursors] = useState<Array<string | undefined>>([undefined]);
  const [page, setPage] = useState(0);

  useEffect(() => {
    setCursors([undefined]);
    setPage(0);
  }, [orgId]);

  const query = useQuery({
    queryKey: ["operator", "access", orgId, keyName, page],
    enabled: Boolean(orgId),
    placeholderData: keepPreviousData,
    queryFn: async (): Promise<{ rows: T[]; nextCursor: string | null }> => {
      const response = await api.get<unknown>(path, {
        requiresOrg: false,
        includeOrg: false,
        query: { limit: 25, cursor: cursors[page] },
      });
      if (!isPage(response, keyName, isValidRow)) {
        throw invalidContract(`The ${keyName} response did not match the reviewed E04A contract.`);
      }
      return { rows: response[keyName] as T[], nextCursor: response.next_cursor as string | null };
    },
  });

  return {
    rows: query.data?.rows ?? [],
    isPending: query.isPending,
    isError: query.isError,
    error: query.error,
    page,
    hasNext: (query.data?.nextCursor ?? null) !== null,
    hasPrevious: page > 0,
    goNext: () => {
      const next = query.data?.nextCursor;
      if (!next) return;
      setCursors((current) => {
        const stack = [...current];
        stack[page + 1] = next;
        return stack;
      });
      setPage(page + 1);
    },
    goPrevious: () => setPage((current) => Math.max(0, current - 1)),
    refetch: () => void query.refetch(),
  };
}

function AccessError({ error, onRetry }: { error: unknown; onRetry: () => void }) {
  if (isApiError(error) && error.httpStatus === 403) return <EmptyState title="You do not have permission to view access assignments." body="The server refused this operator request." />;
  return <ErrorState error={error} onRetry={onRetry} />;
}

function matchRow(value: string, filter: string): boolean {
  return value.toLocaleLowerCase().includes(filter);
}

function PagedPanel<T>({
  title,
  emptyTitle,
  filter,
  pager,
  rowMatches,
  desktopRows,
  mobileRows,
}: {
  title: string;
  emptyTitle: string;
  filter: string;
  pager: AccessPager<T>;
  rowMatches: (row: T, filter: string) => boolean;
  desktopRows: (rows: T[]) => DataTableRow[];
  mobileRows: (rows: T[]) => MobileRow[];
}) {
  const filtered = pager.rows.filter((row) => rowMatches(row, filter));
  let body: ReactNode;
  if (pager.isPending) {
    body = <LoadingState label={`Loading ${title.toLowerCase()}…`} />;
  } else if (pager.isError) {
    body = <AccessError error={pager.error} onRetry={pager.refetch} />;
  } else if (filtered.length === 0) {
    body = <EmptyState title={emptyTitle} body="Change the filter or assign a new record." />;
  } else {
    body = (
      <>
        <div className="hidden md:block">
          <DataTable
            columns={[
              { key: "access", label: "Access" },
              { key: "status", label: "Status" },
              { key: "why", label: "Why" },
              { key: "effective", label: "Effective from" },
              { key: "actions", label: "Actions" },
            ]}
            rows={desktopRows(filtered)}
            label={`${title} desktop view`}
            density="compact"
          />
        </div>
        <MobileRows label={`${title} mobile view`} className="md:hidden" rows={mobileRows(filtered)} />
        <Pagination
          label={`${title} pagination`}
          page={pager.page}
          hasPrevious={pager.hasPrevious}
          hasNext={pager.hasNext}
          onPrevious={pager.goPrevious}
          onNext={pager.goNext}
        />
      </>
    );
  }
  return (
    <div className="flex flex-col gap-3">
      <h3 className="text-base font-semibold text-ink">{title}</h3>
      {body}
    </div>
  );
}

export function AccessSection({ filter, orgId }: { filter: string; orgId: string }) {
  const api = useApiClient();
  const queryClient = useQueryClient();
  const { push } = useToast();
  // Story PS1: the organisation selector lives at the console header (OperatorPage); this
  // section receives the chosen organisation and consumes the SAME cached read through the
  // shared hook, so its loading, permission and no-active-organisation states stay here.
  const organisations = useOperatorOrganisations();
  // Tour debt (Story N3): ACTIVE organisations only. A suspended or closed organisation is
  // not an assignable target; offering it let an operator select an org whose next mutation
  // was always going to be refused.
  const activeOrganisations = useMemo(
    () => (organisations.data ?? []).filter((organisation) => organisation.status === "active"),
    [organisations.data],
  );
  const [dialog, setDialog] = useState<DialogState | null>(null);
  const [coverage, setCoverage] = useState<AdminCampaignSubscription | null>(null);
  const coverageInvoker = useRef<HTMLElement | null>(null);
  useEffect(() => { setCoverage(null); }, [orgId]);
  useEffect(() => {
    if (coverage !== null) return;
    if (coverageInvoker.current?.isConnected) coverageInvoker.current.focus();
    coverageInvoker.current = null;
  }, [coverage]);
  const closeCoverage = () => setCoverage(null);
  const coverageAction = (row: AdminModuleSubscription | AdminUserModuleGrant | AdminCampaignSubscription, mobile = false) =>
    "campaign_name" in row && row.module_code === "high-intent" && row.status === "active" ? (
      <Button type="button" size="sm" variant="secondary" className="h-11 min-w-11" disabled={mutation.isPending} aria-label={`Delivery coverage for ${row.campaign_name}${mobile ? " in mobile view" : ""}`} onClick={event => { coverageInvoker.current = event.currentTarget; setCoverage(row); }}>Delivery coverage</Button>
    ) : null;
  const relinkAction = (row: AdminModuleSubscription | AdminUserModuleGrant | AdminCampaignSubscription, mobile = false) =>
    "campaign_name" in row && row.status === "active" ? (
      <Button type="button" size="sm" variant="secondary" className="h-11 min-w-11" disabled={mutation.isPending} aria-label={`Re-link ${assignmentName(row)}${mobile ? " in mobile view" : ""}`} onClick={(event) => openDialog({ action: "relink", kind: "campaign", row }, event.currentTarget)}>Re-link</Button>
    ) : null;
  const [form, setForm] = useState<FormState>(() => initialForm("module"));
  const [mutationError, setMutationError] = useState<string | null>(null);
  const dialogInvoker = useRef<HTMLElement | null>(null);

  const membersPager = useAccessPage<AdminMember>(orgId, `/admin/organisations/${encodeURIComponent(orgId)}/members`, "members", isMember);
  const modulesPager = useAccessPage<AdminModuleSubscription>(orgId, `/admin/access/organisations/${encodeURIComponent(orgId)}/module-subscriptions`, "module_subscriptions", isModuleSubscription);
  const grantsPager = useAccessPage<AdminUserModuleGrant>(orgId, `/admin/access/organisations/${encodeURIComponent(orgId)}/user-module-grants`, "user_module_grants", isGrant);
  const campaignsPager = useAccessPage<AdminCampaignSubscription>(orgId, `/admin/access/organisations/${encodeURIComponent(orgId)}/campaign-subscriptions`, "campaign_subscriptions", isCampaign);

  const data: AccessData = useMemo(
    () => ({
      members: membersPager.rows,
      modules: modulesPager.rows,
      grants: grantsPager.rows,
      campaigns: campaignsPager.rows,
    }),
    [membersPager.rows, modulesPager.rows, grantsPager.rows, campaignsPager.rows],
  );

  // The dialog's choosers need COMPLETE option sets; the seam can only provide those by
  // walking the pagers, so the walk happens once, on demand, while the dialog is open.
  const dialogOptions = useQuery({
    queryKey: ["operator", "access", orgId, "dialog-options"],
    enabled: dialog !== null && Boolean(orgId),
    queryFn: async () => {
      const encodedOrgId = encodeURIComponent(orgId);
      const [membersAll, subscriptionsAll, catalogueModules, catalogueCampaigns] = await Promise.all([
        readAll(api, `/admin/organisations/${encodedOrgId}/members`, "members", isMember),
        readAll(api, `/admin/access/organisations/${encodedOrgId}/module-subscriptions`, "module_subscriptions", isModuleSubscription),
        readCatalogueKind(api, "module"),
        readCatalogueKind(api, "campaign"),
      ]);
      return { membersAll, subscriptionsAll, catalogueModules, catalogueCampaigns };
    },
  });

  const mutation = useMutation({
    mutationFn: async ({ state, values, kind }: { state: DialogState; values: FormState; kind: AssignmentKind }) => {
      const suffix = kind === "module" ? "module-subscriptions" : kind === "grant" ? "user-module-grants" : "campaign-subscriptions";
      if (state.action === "relink") {
        const response = await api.post<unknown>(`/admin/access/campaign-subscriptions/${state.row.id}/anchor`, { reason: values.reason }, { requiresOrg: false, includeOrg: false });
        const parsed = readRelinkResponse(response, state.row.id);
        if (!parsed) throw invalidContract("The re-link response did not match the reviewed contract.");
        return parsed;
      }
      if (state.action === "revoke") {
        const response = await api.delete<unknown>(`/admin/access/${suffix}/${state.row.id}`, { expected_version: state.row.version, reason: values.reason }, { requiresOrg: false, includeOrg: false });
        const parsed = readMutationResponse(response, kind, "revoke");
        if (!parsed) throw invalidContract("The access revoke response did not match the reviewed E04 contract.");
        return parsed;
      }
      const interval = { effective_from: values.effectiveFrom, ...(values.effectiveTo ? { effective_to: values.effectiveTo } : {}) };
      if (state.action === "edit") {
        const body = kind === "grant"
          ? { ...interval, expected_version: state.row.version, reason: values.reason }
          : { ...interval, status: values.status, expected_version: state.row.version, reason: values.reason };
        const response = await api.patch<unknown>(`/admin/access/${suffix}/${state.row.id}`, body, { requiresOrg: false, includeOrg: false });
        const parsed = readMutationResponse(response, kind, "edit");
        if (!parsed) throw invalidContract("The access update response did not match the reviewed E04 contract.");
        return parsed;
      }
      const body = kind === "module"
        ? { org_id: orgId, module_id: values.moduleId, status: values.status, ...interval, reason: values.reason }
        : kind === "grant"
        ? { org_id: orgId, module_id: values.moduleId, module_subscription_id: values.moduleSubscriptionId, user_id: values.memberId, ...interval, reason: values.reason }
        : { org_id: orgId, module_subscription_id: values.moduleSubscriptionId, module_id: values.moduleId, campaign_id: values.campaignId, status: values.status, ...interval, reason: values.reason };
      const response = await api.post<unknown>(`/admin/access/${suffix}`, body, { requiresOrg: false, includeOrg: false });
      const parsed = readMutationResponse(response, kind, "create");
      if (!parsed) throw invalidContract("The access create response did not match the reviewed E04 contract.");
      return parsed;
    },
    onSuccess: (response, variables) => {
      const action = variables.state.action === "revoke" ? "revoked" : variables.state.action === "edit" ? "updated" : variables.state.action === "relink" ? "re-linked" : "created";
      const verdict = response.verdict;
      if (verdict === null) {
        push({ tone: "success", title: `Access assignment ${action}` });
      } else if (variables.state.action === "relink") {
        // H-1: a Re-link re-applies the STORED coverage, so its answer is read for coverage
        // exactly as the assignment panel reads it. A link without confirmed coverage is
        // never reported as "active".
        const notice = relinkCoverageNotice(relinkCoverageOf(response));
        push({ tone: notice.tone, title: `Access assignment ${action}`, body: notice.body });
      } else if (verdict.link === "anchor") {
        push(verdict.ok
          ? { tone: "success", title: `Access assignment ${action}`, body: "The HIGH-INTENT delivery link is active." }
          : { tone: "warning", title: `Access assignment ${action}`, body: "Assignment saved; the HIGH-INTENT delivery link did not attach. Use Re-link or contact support." });
      } else {
        push(verdict.ok
          ? { tone: "success", title: `Access assignment ${action}`, body: "The HIGH-INTENT delivery link has been retired." }
          : { tone: "warning", title: `Access assignment ${action}`, body: `Assignment revoked; the HIGH-INTENT delivery link was not retired. ${verdict.reason ? `${RETIRE_REASON_COPY[verdict.reason]} ` : ""}Contact support to confirm it is closed.` });
      }
      setDialog(null);
      setMutationError(null);
      void queryClient.invalidateQueries({ queryKey: ["operator", "access"] });
    },
    onError: (error) => setMutationError(errorCopy(error)),
  });

  useEffect(() => {
    if (dialog !== null || mutation.isPending) return;
    const invoker = dialogInvoker.current;
    dialogInvoker.current = null;
    if (invoker?.isConnected) invoker.focus();
  }, [dialog, mutation.isPending]);

  const openDialog = (state: DialogState, invoker: HTMLElement) => {
    if (mutation.isPending) return;
    dialogInvoker.current = invoker;
    setDialog(state);
    setMutationError(null);
    setForm(initialForm(state.kind, data, state.action === "create" ? undefined : state.row));
  };

  const submitDialog = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!dialog || mutation.isPending) return;
    const problem = formError(dialog, form, orgId);
    if (problem) {
      setMutationError(problem);
      return;
    }
    const values = { ...form, reason: form.reason.trim() };
    mutation.mutate({ state: dialog, values, kind: mutationKind(dialog, values) });
  };

  const changeCreateKind = (kind: AssignmentKind) => {
    if (!dialog || dialog.action !== "create" || mutation.isPending) return;
    setDialog({ action: "create", kind });
    setForm(initialForm(kind, data));
    setMutationError(null);
  };

  const closeDialog = () => {
    if (!mutation.isPending) setDialog(null);
  };

  if (organisations.isLoading) return <LoadingState label="Loading organisations…" />;
  if (organisations.isError) return <AccessError error={organisations.error} onRetry={() => void organisations.refetch()} />;
  if (activeOrganisations.length === 0) return <EmptyState title="No active organisations" body="No organisation is currently active, so access cannot be assigned. Suspended and closed organisations stay hidden here." />;
  if (!orgId) {
    return (
      <EmptyState
        title="Choose an organisation"
        body="Choose an organisation above to review and administer its access assignments."
      />
    );
  }

  const selected = activeOrganisations.find((organisation) => organisation.id === orgId);

  const memberDesktop = (rows: AdminMember[]): DataTableRow[] =>
    rows.map((member) => ({
      id: member.user_id,
      access: (
        <div className="flex flex-col">
          <span className="font-medium">{member.full_name ?? "Unnamed member"}</span>
          <span className="text-xs text-ink-muted">{member.email ?? "No email"}</span>
        </div>
      ),
    }));

  const memberMobile = (rows: AdminMember[]): MobileRow[] =>
    rows.map((member) => ({
      id: member.user_id,
      name: member.full_name ?? "Unnamed member",
      meta: member.email ?? "No email",
    }));

  const assignmentDesktop = (rows: Array<AdminModuleSubscription | AdminUserModuleGrant | AdminCampaignSubscription>): DataTableRow[] =>
    rows.map((row) => ({
      id: row.id,
      access: (
        <div className="flex flex-col">
          <span className="font-medium">{assignmentDisplayName(row)}</span>
          <span className="font-mono text-xs text-ink-muted">{assignmentCode(row)}</span>
        </div>
      ),
      status: (
        <Chip signal={statusSignal(row.status)} dot={row.status === "active"}>
          {row.status}
        </Chip>
      ),
      why: <span className="text-xs text-ink-muted">{whyLine(row)}</span>,
      effective: formatDateTime(row.effective_from),
      actions: (
        <div className="flex flex-wrap gap-2">
          {coverageAction(row)}
          {relinkAction(row)}
          <Button type="button" size="sm" variant="secondary" className="h-11 min-w-11" disabled={mutation.isPending} onClick={(event) => openDialog({ action: "edit", kind: rowKindOf(row), row }, event.currentTarget)} aria-label={`Edit ${assignmentName(row)}`}>Edit</Button>
          <Button type="button" size="sm" variant="secondary" className="h-11 min-w-11" disabled={mutation.isPending} onClick={(event) => openDialog({ action: "revoke", kind: rowKindOf(row), row }, event.currentTarget)} aria-label={`Revoke ${assignmentName(row)}`}>Revoke</Button>
        </div>
      ),
    }));

  const assignmentMobile = (rows: Array<AdminModuleSubscription | AdminUserModuleGrant | AdminCampaignSubscription>): MobileRow[] =>
    rows.map((row) => ({
      id: row.id,
      name: assignmentDisplayName(row),
      meta: `${assignmentCode(row)} · ${whyLine(row)}`,
      signal: statusSignal(row.status),
      signalText: row.status,
      actions: (
        <>
          {coverageAction(row, true)}
          {relinkAction(row, true)}
          <Button type="button" size="sm" variant="secondary" className="h-11 min-w-11" disabled={mutation.isPending} onClick={(event) => openDialog({ action: "edit", kind: rowKindOf(row), row }, event.currentTarget)} aria-label={`Edit ${assignmentName(row)} in mobile view`}>Edit</Button>
          <Button type="button" size="sm" variant="secondary" className="h-11 min-w-11" disabled={mutation.isPending} onClick={(event) => openDialog({ action: "revoke", kind: rowKindOf(row), row }, event.currentTarget)} aria-label={`Revoke ${assignmentName(row)} in mobile view`}>Revoke</Button>
        </>
      ),
    }));

  return (
    <section aria-labelledby="access-heading" className="flex flex-col gap-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div><h2 id="access-heading" className="text-lg font-semibold text-ink">Access assignments</h2><p className="mt-1 text-sm text-ink-muted">What an organisation can use, and who in it: server-backed subscriptions, user grants and campaign history.</p></div>
        <Button type="button" size="lg" onClick={(event) => openDialog({ action: "create", kind: "module" }, event.currentTarget)} disabled={mutation.isPending}>Assign access</Button>
      </div>
      <p className="text-sm text-ink-muted">Selected organisation: <span className="font-medium text-ink">{selected?.name}</span></p>
      <PagedPanel
        title="Members"
        emptyTitle="No members match this organisation and filter."
        filter={filter}
        pager={membersPager}
        rowMatches={(member, value) => matchRow(`${member.full_name ?? ""} ${member.email ?? ""}`, value)}
        desktopRows={memberDesktop}
        mobileRows={memberMobile}
      />
      <PagedPanel
        title="Module subscriptions"
        emptyTitle="No module subscriptions match this organisation and filter."
        filter={filter}
        pager={modulesPager}
        rowMatches={(row, value) => matchRow(`${row.module_name} ${row.module_code} ${row.status}`, value)}
        desktopRows={assignmentDesktop}
        mobileRows={assignmentMobile}
      />
      <PagedPanel
        title="User module grants"
        emptyTitle="No user grants match this organisation and filter."
        filter={filter}
        pager={grantsPager}
        rowMatches={(row, value) => matchRow(`${row.full_name ?? ""} ${row.email ?? ""} ${row.module_name} ${row.module_code} ${row.status}`, value)}
        desktopRows={assignmentDesktop}
        mobileRows={assignmentMobile}
      />
      <PagedPanel
        title="Campaign subscriptions"
        emptyTitle="No campaign subscriptions match this organisation and filter."
        filter={filter}
        pager={campaignsPager}
        rowMatches={(row, value) => matchRow(`${row.campaign_name} ${row.campaign_code} ${row.module_name} ${row.status}`, value)}
        desktopRows={assignmentDesktop}
        mobileRows={assignmentMobile}
      />
      {coverage && coverage.org_id === orgId ? <ClientDeliveryCoverage key={`${orgId}:${coverage.id}:${coverage.version}`} orgId={orgId} grantId={coverage.id} grantVersion={coverage.version} organisationName={selected?.name ?? orgId} campaignName={coverage.campaign_name} onClose={closeCoverage} /> : null}
      {dialog ? <AssignmentDialog state={dialog} form={form} setForm={setForm} data={data} dialogOptions={dialogOptions.data ?? null} optionsPending={dialogOptions.isPending} pending={mutation.isPending} error={mutationError} onClose={closeDialog} onCreateKindChange={changeCreateKind} onSubmit={submitDialog} /> : null}
    </section>
  );
}

function rowKindOf(row: AdminModuleSubscription | AdminUserModuleGrant | AdminCampaignSubscription): AssignmentKind {
  return "campaign_name" in row ? "campaign" : "user_id" in row ? "grant" : "module";
}

interface DialogOptions {
  membersAll: AdminMember[];
  subscriptionsAll: AdminModuleSubscription[];
  catalogueModules: Awaited<ReturnType<typeof readCatalogueKind>>;
  catalogueCampaigns: Awaited<ReturnType<typeof readCatalogueKind>>;
}

function AssignmentDialog({ state, form, setForm, data, dialogOptions, optionsPending, pending, error, onClose, onCreateKindChange, onSubmit }: { state: DialogState; form: FormState; setForm: Dispatch<SetStateAction<FormState>>; data: AccessData; dialogOptions: DialogOptions | null; optionsPending: boolean; pending: boolean; error: string | null; onClose: () => void; onCreateKindChange: (kind: AssignmentKind) => void; onSubmit: (event: FormEvent<HTMLFormElement>) => void }) {
  const label = kindLabel(state.kind);
  const update = (key: keyof FormState) => (event: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => setForm((current) => ({ ...current, [key]: event.target.value }));
  const isRelink = state.action === "relink";
  const isRevoke = state.action === "revoke" || isRelink;
  const subscriptionRows = dialogOptions?.subscriptionsAll ?? data.modules;
  const memberRows = dialogOptions?.membersAll ?? data.members;
  const subscriptionOptions = subscriptionRows.map((row) => ({ row, eligible: isCurrentActiveSubscription(row) }));
  const catalogueModules = dialogOptions?.catalogueModules ?? [];
  const catalogueCampaigns = dialogOptions?.catalogueCampaigns ?? [];
  return <Dialog open onClose={onClose} closeDisabled={pending} busy={pending} title={isRelink ? `Re-link ${label}` : isRevoke ? `Revoke ${label}` : state.action === "edit" ? `Edit ${label}` : "Assign access"} description={isRelink ? "This asks the delivery partner to attach the link again. Confirm with a reason." : isRevoke ? "This retains the assignment history. Confirm with a reason." : "The server validates hierarchy, authority and the effective interval."} footer={<><Button type="button" variant="secondary" className="h-11" onClick={onClose} disabled={pending}>Cancel</Button><Button type="submit" form="access-assignment-form" className="h-11" loading={pending}>{isRelink ? "Re-link delivery" : isRevoke ? "Revoke assignment" : state.action === "edit" ? "Save changes" : "Create assignment"}</Button></>}><form id="access-assignment-form" className="flex flex-col gap-4" onSubmit={onSubmit}>{pending ? <p role="status" aria-live="polite" className="text-sm text-ink-muted">Saving access assignment. Please wait.</p> : null}{state.action === "create" ? <Select label="Assignment type" value={form.kind} onChange={(event) => onCreateKindChange(event.target.value as AssignmentKind)} className="h-11" disabled={pending}><option value="module">Module subscription</option><option value="grant">User module grant</option><option value="campaign">Campaign subscription</option></Select> : null}{state.action === "edit" ? <IdentityFields kind={state.kind} form={form} /> : null}{!isRevoke && state.action === "create" && form.kind === "module" ? <ModuleChooser moduleId={form.moduleId} modules={catalogueModules} onChange={update("moduleId")} disabled={pending} /> : null}{!isRevoke && state.action === "create" && form.kind === "grant" ? <><Select label="Member" value={form.memberId} onChange={update("memberId")} className="h-11" disabled={pending}><option value="">Choose a member</option>{memberRows.map((member) => <option key={member.user_id} value={member.user_id}>{member.full_name ?? member.email ?? member.user_id}</option>)}</Select><ParentSubscriptionSelect options={subscriptionOptions} value={form.moduleSubscriptionId} setForm={setForm} disabled={pending} /></> : null}{!isRevoke && state.action === "create" && form.kind === "campaign" ? <><ParentSubscriptionSelect options={subscriptionOptions} value={form.moduleSubscriptionId} setForm={setForm} disabled={pending} /><Select label="Campaign" value={form.campaignId} onChange={update("campaignId")} className="h-11" disabled={pending}><option value="">Choose a campaign</option>{catalogueCampaigns.map((item) => <option key={item.id} value={item.id}>{item.name} ({item.code})</option>)}</Select></> : null}{!isRevoke && form.kind !== "grant" ? <Select label="Status" value={form.status} onChange={update("status")} className="h-11" disabled={pending}><option value="active">Active</option><option value="suspended">Suspended</option></Select> : null}{!isRevoke ? <><Input label="Effective from" value={form.effectiveFrom} onChange={update("effectiveFrom")} className="h-11" disabled={pending} hint="RFC3339 offset date-time, for example 2026-09-19T12:00:00.000Z" required /><Input label="Effective to" value={form.effectiveTo} onChange={update("effectiveTo")} className="h-11" disabled={pending} hint="Leave empty for an open-ended interval." /></> : null}<Input label="Reason" value={form.reason} onChange={update("reason")} className="h-11" disabled={pending} minLength={3} maxLength={500} required {...(error ? { error } : {})} />{optionsPending && !isRevoke ? <p role="status" className="text-xs text-ink-muted">Loading all options…</p> : null}</form></Dialog>;
}

function ModuleChooser({ moduleId, modules, onChange, disabled }: { moduleId: string; modules: Awaited<ReturnType<typeof readCatalogueKind>>; onChange: (event: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void; disabled: boolean }) {
  const known = modules.some((item) => item.id === moduleId);
  return (
    <Select label="Module" value={moduleId} onChange={onChange} className="h-11" disabled={disabled} hint="Choose from the catalogue; the server verifies the module's whole ancestor chain.">
      <option value="">Choose a module</option>
      {!known && moduleId ? <option value={moduleId}>Current module (identifier on file)</option> : null}
      {modules.map((item) => <option key={item.id} value={item.id}>{item.name} ({item.code})</option>)}
    </Select>
  );
}

function IdentityFields({ kind, form }: { kind: AssignmentKind; form: FormState }) {
  return <div className="flex flex-col gap-4"><p className="text-sm text-ink-muted">Identity is immutable after creation.</p>{kind === "module" ? <Input label="Module ID" value={form.moduleId} className="h-11" readOnly /> : null}{kind === "grant" ? <><Input label="Member ID" value={form.memberId} className="h-11" readOnly /><Input label="Module subscription ID" value={form.moduleSubscriptionId} className="h-11" readOnly /></> : null}{kind === "campaign" ? <><Input label="Module subscription ID" value={form.moduleSubscriptionId} className="h-11" readOnly /><Input label="Campaign ID" value={form.campaignId} className="h-11" readOnly /></> : null}</div>;
}

function ParentSubscriptionSelect({ options, value, setForm, disabled }: { options: Array<{ row: AdminModuleSubscription; eligible: boolean }>; value: string; setForm: Dispatch<SetStateAction<FormState>>; disabled: boolean }) {
  return <><Select label="Module subscription" value={value} onChange={(event) => { const subscription = options.find(({ row }) => row.id === event.target.value)?.row; setForm((current) => ({ ...current, moduleSubscriptionId: event.target.value, moduleId: subscription?.module_id ?? "" })); }} className="h-11" disabled={disabled}><option value="">Choose a current module subscription</option>{options.map(({ row, eligible }) => <option key={row.id} value={row.id} disabled={!eligible}>{row.module_name} ({eligible ? "active and current" : "not currently eligible"})</option>)}</Select><p className="text-sm text-ink-muted">Only active, currently effective subscriptions can be selected. The server also verifies the catalogue ancestor chain.</p></>;
}
